/**
 * Compact overlay components used by the mobile immersive Live Room
 * (both viewer and host consoles).
 */

/* ------------- Milestone strip: 5 dots + current tier + price ------------- */
export function MilestoneStripMini({ milestones = [], viewers = 0, currentPrice }) {
  const sorted = [...milestones].sort((a, b) => a.threshold - b.threshold);
  let activeIdx = -1;
  sorted.forEach((m, i) => { if (viewers >= m.threshold) activeIdx = i; });

  return (
    <div
      className="bg-[#FFFFFF]/85 backdrop-blur border border-[#E5E1D8] px-2.5 py-2 flex items-center gap-2"
      data-testid="milestone-strip-mini"
    >
      <div className="flex items-center gap-1">
        {sorted.map((m, i) => {
          const reached = viewers >= m.threshold;
          return (
            <span
              key={`ms-${m.threshold}`}
              className={`w-2 h-2 rounded-full transition-all ${
                reached
                  ? "bg-green-400 shadow-[0_2px_6px_rgba(22,163,74,0.15)]"
                  : "bg-[#E5E1D8]"
              } ${activeIdx === i ? "ring-1 ring-[#FF6A00] ring-offset-1 ring-offset-[#F8F7F2]" : ""}`}
              aria-label={`Tier ${i + 1} ${reached ? "reached" : "locked"}`}
            />
          );
        })}
      </div>
      <div className="flex flex-col leading-tight min-w-0">
        <span className="font-mono-ds text-[9px] uppercase tracking-widest text-[#6E6E6E]">
          Tier {activeIdx + 1 || 0}/{sorted.length} · {viewers.toLocaleString()} viewing
        </span>
        {currentPrice != null && (
          <span className="font-mono-ds text-[13px] font-bold text-[#FF6A00] tabular-nums leading-none mt-0.5">
            ₹{Number(currentPrice).toLocaleString("en-IN")}
          </span>
        )}
      </div>
    </div>
  );
}

/* ------------- Product pill: small floating card with image + name ------------- */
export function ProductPill({ product }) {
  if (!product) return null;
  return (
    <div
      className="bg-[#FFFFFF]/90 backdrop-blur border border-[#E5E1D8] pr-2 pl-1 py-1 flex items-center gap-2 w-full min-w-0 shadow-[0_2px_6px_rgba(17,17,17,0.05)]"
      data-testid="product-pill"
    >
      {product.image_url && (
        <div className="w-8 h-8 bg-[#FFFFFF] overflow-hidden flex-shrink-0">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="brand-tiny text-[#6E6E6E] leading-none">// PRODUCT</div>
        <div className="font-display text-[12px] uppercase tracking-tight text-[#161616] leading-tight truncate">
          {product.name}
        </div>
      </div>
    </div>
  );
}

/* ------------- Status pill (LIVE, SCHEDULED, ENDED) ------------- */
export function StatusPillMini({ status }) {
  const map = {
    scheduled: { label: "SCHEDULED", cls: "border-[#E5E1D8] text-[#6E6E6E] bg-[#FFFFFF]/85" },
    open: { label: "● WAITING", cls: "border-[#FF6A00] text-[#FF6A00] bg-[#FFFFFF]/85" },
    live: { label: "● LIVE", cls: "border-[#FF6A00] text-[#161616] bg-[#FF6A00] font-bold" },
    final_window: { label: "FINAL WINDOW", cls: "border-[#FF6A00] text-[#FF6A00] bg-[#FFFFFF]/85" },
    ended: { label: "ENDED", cls: "border-[#E5E1D8] text-[#6E6E6E] bg-[#FFFFFF]/85" },
  };
  const m = map[status] || map.scheduled;
  return (
    <div
      className={`font-mono-ds text-[9px] uppercase tracking-widest px-1.5 py-1 border backdrop-blur ${m.cls}`}
      data-testid={`status-pill-mini-${status}`}
    >
      {m.label}
    </div>
  );
}

/* ------------- Viewer count chip ------------- */
export function ViewersChip({ n }) {
  return (
    <div
      className="bg-[#FFFFFF]/85 backdrop-blur border border-[#E5E1D8] px-1.5 py-1 flex items-center gap-1 font-mono-ds text-[10px] uppercase tracking-widest"
      data-testid="viewers-chip"
    >
      <span className="w-1.5 h-1.5 bg-[#7ED957] pulse-dot" aria-hidden />
      <span className="text-[#161616] tabular-nums font-bold">{Number(n || 0).toLocaleString()}</span>
    </div>
  );
}

/* ------------- Compact countdown chip (mm:ss) ------------- */
import { useEffect, useState } from "react";
export function CountdownChip({ to, label, accent = true, onComplete }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const target = new Date(to).getTime();
  const diff = Math.max(0, target - now);
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  useEffect(() => { if (diff <= 0 && onComplete) onComplete(); }, [diff, onComplete]);
  return (
    <div
      className={`px-2 py-1 backdrop-blur border ${
        accent ? "border-[#FF6A00] bg-[#FFFFFF]/95 shadow-[0_4px_12px_rgba(255,106,0,0.15)]" : "border-[#E5E1D8] bg-[#FFFFFF]/85"
      }`}
      data-testid="countdown-chip"
    >
      <div className="font-mono-ds text-[8px] uppercase tracking-widest text-[#6E6E6E] leading-none">
        {label}
      </div>
      <div className={`font-display text-lg leading-none tracking-tighter tabular-nums mt-0.5 ${accent ? "text-[#FF6A00]" : "text-[#161616]"}`}>
        {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
      </div>
    </div>
  );
}


/* ------------- Slim mobile progress strip (top-of-screen lime bar) ------------- */
export function MobileLiveProgressStrip({ viewers = 0, milestones = [] }) {
  if (!milestones || milestones.length === 0) return null;
  const maxThreshold = Math.max(...milestones.map((m) => Number(m.threshold || 0)));
  if (!maxThreshold) return null;
  const pct = Math.min(100, Math.max(0, (Number(viewers || 0) / maxThreshold) * 100));
  const hitIdx = milestones.reduce(
    (acc, m, i) => (Number(viewers) >= Number(m.threshold) ? i : acc),
    -1
  );
  const next = milestones[Math.min(hitIdx + 1, milestones.length - 1)];
  const nextGap = Math.max(0, Number(next?.threshold || 0) - Number(viewers || 0));

  return (
    <div
      className="w-full bg-[#FFFFFF]/95 backdrop-blur border-b border-[#161616]"
      data-testid="mobile-live-progress-strip"
    >
      {/* Meta row — very compact */}
      <div className="flex items-center justify-between px-3 pt-1 pb-0.5">
        <span className="font-mono-ds text-[9px] uppercase tracking-[0.20em] text-[#6E6E6E] leading-none">
          <span className="text-[#264733] font-bold">Live</span>
          <span className="opacity-40 mx-1">·</span>
          <span className="text-[#161616] tabular-nums font-bold">{Number(viewers).toLocaleString("en-IN")}</span>
        </span>
        <span className="font-mono-ds text-[9px] uppercase tracking-[0.20em] text-[#6E6E6E] leading-none tabular-nums">
          {hitIdx + 1 < milestones.length ? (
            <>Next in <span className="text-[#FF6A00] font-bold">{nextGap.toLocaleString("en-IN")}</span></>
          ) : (
            <span className="text-[#264733] font-bold">Floor unlocked</span>
          )}
        </span>
      </div>
      {/* Bar + tick marks */}
      <div className="relative h-2 bg-[#F0EDE4]">
        <div
          className="absolute left-0 top-0 bottom-0 bg-[#B9E641] transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          data-testid="mobile-live-progress-fill"
        />
        {milestones.map((m, i) => {
          const at = Math.min(100, (Number(m.threshold) / maxThreshold) * 100);
          const hit = i <= hitIdx;
          return (
            <span
              key={i}
              className={`absolute top-0 bottom-0 w-[2px] ${hit ? "bg-[#264733]" : "bg-[#161616]/40"}`}
              style={{ left: `${at}%`, transform: "translateX(-1px)" }}
            />
          );
        })}
      </div>
    </div>
  );
}
